import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import PasswordInput from '../components/PasswordInput';
import ConnectorsPanel from '../components/warehouse/ConnectorsPanel';
import ApiKeysPanel from '../components/ApiKeysPanel';
import Icon from '../design-system/primitives/Icon';
import Btn from '../design-system/primitives/Btn';
import ThemeToggle from '../design-system/primitives/ThemeToggle';
import './AccountPage.css';

const INDUSTRY_OPTIONS = [
  { value: '', label: 'Select industry…' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail & E-Commerce' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'energy', label: 'Energy & Utilities' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'transportation', label: 'Transportation & Logistics' },
  { value: 'government', label: 'Government & Public Sector' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'other', label: 'Other' },
];

const SIZE_OPTIONS = [
  { value: '', label: 'Select size…' },
  { value: '1-10', label: '1–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-500', label: '201–500' },
  { value: '501-1000', label: '501–1,000' },
  { value: '1001-5000', label: '1,001–5,000' },
  { value: '5001+', label: '5,001+' },
];

const COUNTRY_OPTIONS = [
  { value: '', label: 'Select country…' },
  { value: 'albania', label: 'Albania' }, { value: 'andorra', label: 'Andorra' },
  { value: 'australia', label: 'Australia' }, { value: 'austria', label: 'Austria' },
  { value: 'belarus', label: 'Belarus' }, { value: 'belgium', label: 'Belgium' },
  { value: 'bosnia_and_herzegovina', label: 'Bosnia and Herzegovina' },
  { value: 'bulgaria', label: 'Bulgaria' }, { value: 'canada', label: 'Canada' },
  { value: 'china', label: 'China' }, { value: 'croatia', label: 'Croatia' },
  { value: 'cyprus', label: 'Cyprus' }, { value: 'czech_republic', label: 'Czech Republic' },
  { value: 'denmark', label: 'Denmark' }, { value: 'estonia', label: 'Estonia' },
  { value: 'finland', label: 'Finland' }, { value: 'france', label: 'France' },
  { value: 'germany', label: 'Germany' }, { value: 'greece', label: 'Greece' },
  { value: 'hungary', label: 'Hungary' }, { value: 'iceland', label: 'Iceland' },
  { value: 'india', label: 'India' }, { value: 'ireland', label: 'Ireland' },
  { value: 'italy', label: 'Italy' }, { value: 'kosovo', label: 'Kosovo' },
  { value: 'latvia', label: 'Latvia' }, { value: 'liechtenstein', label: 'Liechtenstein' },
  { value: 'lithuania', label: 'Lithuania' }, { value: 'luxembourg', label: 'Luxembourg' },
  { value: 'malta', label: 'Malta' }, { value: 'moldova', label: 'Moldova' },
  { value: 'monaco', label: 'Monaco' }, { value: 'montenegro', label: 'Montenegro' },
  { value: 'netherlands', label: 'Netherlands' }, { value: 'north_macedonia', label: 'North Macedonia' },
  { value: 'norway', label: 'Norway' }, { value: 'poland', label: 'Poland' },
  { value: 'portugal', label: 'Portugal' }, { value: 'qatar', label: 'Qatar' },
  { value: 'romania', label: 'Romania' }, { value: 'russia', label: 'Russia' },
  { value: 'san_marino', label: 'San Marino' }, { value: 'serbia', label: 'Serbia' },
  { value: 'slovakia', label: 'Slovakia' }, { value: 'slovenia', label: 'Slovenia' },
  { value: 'spain', label: 'Spain' }, { value: 'sweden', label: 'Sweden' },
  { value: 'switzerland', label: 'Switzerland' }, { value: 'turkey', label: 'Turkey' },
  { value: 'ukraine', label: 'Ukraine' }, { value: 'uae', label: 'UAE' },
  { value: 'uk', label: 'United Kingdom' }, { value: 'usa', label: 'USA' },
  { value: 'vatican_city', label: 'Vatican City' }, { value: 'other', label: 'Other' },
];

const BASE_TABS = [
  { key: 'organization', label: 'Organization', icon: 'building-2' },
  { key: 'connectors', label: 'Connectors', icon: 'database' },
  { key: 'usage', label: 'Usage', icon: 'bar-chart-3' },
  { key: 'settings', label: 'Account Settings', icon: 'settings' },
];

// The API-keys tab exists only for accounts enabled for production API access,
// so its presence is itself the signal that the account is switched on.
const tabsFor = (productionApiEnabled) => (
  productionApiEnabled
    ? [...BASE_TABS.slice(0, 3), { key: 'api-keys', label: 'API Keys', icon: 'key-round' }, BASE_TABS[3]]
    : BASE_TABS
);

const Wordmark = ({ size = 18, onClick }) => (
  <span
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center',
      color: 'var(--fg-strong)', lineHeight: 1,
      fontFamily: 'var(--font-sans)',
      fontWeight: 600, fontSize: size, letterSpacing: '-0.025em',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    Qombra
  </span>
);

const AccountPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('organization');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '' });
  const [orgForm, setOrgForm] = useState({ company_name: '', country: '', industry: '', num_employees: '' });
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [orgImageFile, setOrgImageFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const profileImageRef = useRef(null);
  const orgImageRef = useRef(null);

  // Derived after `data` exists: the API-keys tab only appears for accounts
  // enabled for production API access.
  const tabs = tabsFor(data?.production_api_enabled);

  const loadData = () =>
    apiService.getAccount().then((res) => {
      setData(res.data);
      const { user, organization } = res.data;
      setProfileForm({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '' });
      if (organization) {
        setOrgForm({
          company_name: organization.company_name || '',
          country: organization.country || '',
          industry: organization.industry || '',
          num_employees: organization.num_employees || '',
        });
      }
    });

  useEffect(() => {
    loadData().catch(() => setError('Failed to load account data.')).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfileSave = async () => {
    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      Object.entries(profileForm).forEach(([k, v]) => fd.append(k, v));
      if (profileImageFile) fd.append('image', profileImageFile);
      await apiService.updateProfile(fd);
      await loadData();
      setProfileImageFile(null);
      setIsEditingProfile(false);
    } catch { setError('Failed to save profile.'); }
    finally { setSaving(false); }
  };

  const handleOrgSave = async () => {
    setSaving(true); setError(null);
    try {
      const fd = new FormData();
      Object.entries(orgForm).forEach(([k, v]) => fd.append(k, v));
      if (orgImageFile) fd.append('image', orgImageFile);
      await apiService.updateOrganization(fd);
      await loadData();
      setOrgImageFile(null);
      setIsEditingOrg(false);
    } catch { setError('Failed to save organization.'); }
    finally { setSaving(false); }
  };

  const handleDeleteAccount = async () => {
    try { await apiService.deleteAccount(); navigate('/'); }
    catch { setError('Failed to delete account.'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwSaving(true);
    try {
      await apiService.changePassword(pwForm.current_password, pwForm.new_password);
      setPwSuccess('Password updated successfully.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setIsChangingPassword(false);
    } catch (err) {
      setPwError(err?.response?.data?.detail || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="account-root">
        <div className="account-loading">Loading…</div>
      </div>
    );
  }

  const { user, customer, organization, usage } = data || {};
  const isPrimary = customer?.is_primary_contact;

  const countryLabel = (val) => COUNTRY_OPTIONS.find((o) => o.value === val)?.label || val || '—';
  const industryLabel = (val) => INDUSTRY_OPTIONS.find((o) => o.value === val)?.label || val || '—';

  /* ============================================================ */
  /* Organization                                                 */
  /* ============================================================ */
  const renderOrganization = () => {
    if (!organization) {
      return <p className="account-empty">No organization associated with this account.</p>;
    }

    const readonlyView = (
      <div className="account-readonly">
        <div className="account-readonly__image-wrap">
          {organization.image_url
            ? <img src={organization.image_url} alt="org" />
            : <Icon name="building-2" size={26} />}
        </div>
        <dl className="account-dl">
          <dt>Company</dt><dd>{organization.company_name || '—'}</dd>
          <dt>Country</dt><dd>{countryLabel(organization.country)}</dd>
          <dt>Industry</dt><dd>{industryLabel(organization.industry)}</dd>
          <dt>Employees</dt><dd>{organization.num_employees || '—'}</dd>
        </dl>
        {isPrimary && (
          <div className="account-readonly__actions">
            <button className="account-btn account-btn--ghost" onClick={() => setIsEditingOrg(true)}>
              <Icon name="pencil" size={13} /> Edit
            </button>
          </div>
        )}
      </div>
    );

    if (!isPrimary || !isEditingOrg) return readonlyView;

    return (
      <div className="account-form">
        <div className="account-image-row">
          <div className="account-image-circle" onClick={() => orgImageRef.current?.click()}>
            {orgImageFile ? <img src={URL.createObjectURL(orgImageFile)} alt="org" />
              : organization.image_url ? <img src={organization.image_url} alt="org" />
              : <Icon name="building-2" size={30} />}
            <div className="account-image-overlay">Change</div>
          </div>
          <input type="file" accept="image/*" ref={orgImageRef} style={{ display: 'none' }}
            onChange={(e) => setOrgImageFile(e.target.files[0])} />
        </div>
        <div className="account-field">
          <label>Company name</label>
          <input value={orgForm.company_name} placeholder="Company name"
            onChange={(e) => setOrgForm((f) => ({ ...f, company_name: e.target.value }))} />
        </div>
        <div className="account-field">
          <label>Country</label>
          <select value={orgForm.country} onChange={(e) => setOrgForm((f) => ({ ...f, country: e.target.value }))}>
            {COUNTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="account-field">
          <label>Industry</label>
          <select value={orgForm.industry} onChange={(e) => setOrgForm((f) => ({ ...f, industry: e.target.value }))}>
            {INDUSTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="account-field">
          <label>Number of employees</label>
          <select value={orgForm.num_employees} onChange={(e) => setOrgForm((f) => ({ ...f, num_employees: e.target.value }))}>
            {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="account-form-actions">
          <button className="account-btn" onClick={handleOrgSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button className="account-btn account-btn--ghost" onClick={() => setIsEditingOrg(false)} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  /* ============================================================ */
  /* Usage                                                        */
  /* ============================================================ */
  const renderUsage = () => {
    const { analyses, chat_messages, output_tokens, is_unlimited, scope, personal, api } = usage || {};
    const isOrgScope = scope === 'organization';

    const UsageStat = ({ used, max, label }) => (
      <div className="account-usage-stat">
        <div className="account-usage-number">
          <span className="account-usage-used">{used ?? '—'}</span>
          <span className="account-usage-sep">/</span>
          <span className="account-usage-max">{is_unlimited ? '∞' : (max ?? '—')}</span>
        </div>
        <div className="account-usage-label">{label}</div>
      </div>
    );

    const outputPct = (!is_unlimited && output_tokens?.max)
      ? Math.min(100, Math.round((output_tokens.used / output_tokens.max) * 100))
      : null;
    const outputBarColor = outputPct === null ? null
      : outputPct >= 90 ? 'var(--danger)'
      : outputPct >= 70 ? 'var(--warn-500)'
      : 'var(--foreground)';

    // A quota line always renders when the section is shown (production API access
    // or staff) — "Unlimited" for an uncapped account, never a bare "/ ∞".
    const quotaLine = (used, max, label) => (
      <span className="account-usage-quota-item">
        {max != null ? `${used ?? 0}/${max} ${label}` : `Unlimited ${label}`}
      </span>
    );

    return (
      <div>
        <h2 className="account-section-title">Console usage</h2>

        {isOrgScope && <p className="account-usage-scope-label">Organisation</p>}
        <div className="account-usage">
          <UsageStat used={analyses?.used} max={analyses?.max} label="analyses used" />
          <div className="account-usage-divider" />
          <UsageStat used={chat_messages?.used} max={chat_messages?.max} label="chat messages used" />
        </div>

        {!is_unlimited && output_tokens && (
          <div className="account-usage-tokens-bar">
            <div className="account-usage-tokens-bar__header">
              <span className="account-usage-tokens-bar__label">Output tokens used</span>
              <span className="account-usage-tokens-bar__count">
                {(output_tokens.used ?? 0).toLocaleString()}
                {output_tokens.max ? ` / ${output_tokens.max.toLocaleString()}` : ''}
              </span>
            </div>
            <div className="account-usage-tokens-bar__track">
              <div
                className="account-usage-tokens-bar__fill"
                style={{ width: `${outputPct ?? 0}%`, background: outputBarColor }}
              />
            </div>
            {outputPct !== null && (
              <div className="account-usage-tokens-bar__pct">{outputPct}% of free tier</div>
            )}
          </div>
        )}

        {isOrgScope && personal && (
          <>
            <p className="account-usage-scope-label account-usage-scope-label--personal">Your personal usage</p>
            <div className="account-usage account-usage--personal">
              <div className="account-usage-stat">
                <div className="account-usage-number">
                  <span className="account-usage-used account-usage-used--sm">{personal.analyses?.used ?? '—'}</span>
                </div>
                <div className="account-usage-label">analyses</div>
              </div>
              <div className="account-usage-divider account-usage-divider--sm" />
              <div className="account-usage-stat">
                <div className="account-usage-number">
                  <span className="account-usage-used account-usage-used--sm">{personal.chat_messages?.used ?? '—'}</span>
                </div>
                <div className="account-usage-label">chat messages</div>
              </div>
            </div>
          </>
        )}

        {api?.enabled && (
          <>
            {/* Only present for accounts with production API access (or staff):
                the backend omits usage.api entirely for everyone else. */}
            <h2 className="account-section-title account-section-title--spaced">API usage and Quotas</h2>
            <div className="account-usage account-usage--personal">
              <div className="account-usage-stat">
                <div className="account-usage-number">
                  <span className="account-usage-used account-usage-used--sm">{api.fits?.total ?? '—'}</span>
                </div>
                <div className="account-usage-label">
                  model fits · {api.fits?.this_month ?? 0} this month
                </div>
              </div>
              <div className="account-usage-divider account-usage-divider--sm" />
              <div className="account-usage-stat">
                <div className="account-usage-number">
                  <span className="account-usage-used account-usage-used--sm">{api.predictions?.total ?? '—'}</span>
                </div>
                <div className="account-usage-label">
                  prediction and forecast calls · {api.predictions?.this_month ?? 0} this month
                </div>
              </div>
            </div>
            <p className="account-usage-quota-line">
              {quotaLine(api.fits?.used_last_hour, api.fits?.max_per_hour, 'fits this hour')}
              <span className="account-usage-quota-sep">·</span>
              {quotaLine(api.predictions?.used_last_hour, api.predictions?.max_per_hour, 'prediction and forecast calls this hour')}
            </p>
          </>
        )}
      </div>
    );
  };

  /* ============================================================ */
  /* Settings (profile, password, danger)                         */
  /* ============================================================ */
  const renderSettings = () => (
    <div>
      {isEditingProfile ? (
        <div className="account-form">
          <div className="account-image-row">
            <div className="account-image-circle" onClick={() => profileImageRef.current?.click()}>
              {profileImageFile ? <img src={URL.createObjectURL(profileImageFile)} alt="you" />
                : user?.image_url ? <img src={user.image_url} alt="you" />
                : <img src="/user.svg" alt="you" />}
              <div className="account-image-overlay">Change</div>
            </div>
            <input type="file" accept="image/*" ref={profileImageRef} style={{ display: 'none' }}
              onChange={(e) => setProfileImageFile(e.target.files[0])} />
          </div>
          <div className="account-field-row">
            <div className="account-field">
              <label>First name</label>
              <input value={profileForm.first_name}
                onChange={(e) => setProfileForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="account-field">
              <label>Last name</label>
              <input value={profileForm.last_name}
                onChange={(e) => setProfileForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="account-field">
            <label>Email</label>
            <input type="email" value={profileForm.email}
              onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="account-form-actions">
            <button className="account-btn" onClick={handleProfileSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="account-btn account-btn--ghost" onClick={() => setIsEditingProfile(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="account-readonly">
          <div className="account-readonly__image-wrap">
            {user?.image_url
              ? <img src={user.image_url} alt="you" />
              : <img src="/user.svg" alt="you" />}
          </div>
          <dl className="account-dl">
            <dt>First name</dt><dd>{user?.first_name || '—'}</dd>
            <dt>Last name</dt><dd>{user?.last_name || '—'}</dd>
            <dt>Email</dt><dd>{user?.email || '—'}</dd>
          </dl>
          <div className="account-readonly__actions">
            <button className="account-btn account-btn--ghost" onClick={() => setIsEditingProfile(true)}>
              <Icon name="pencil" size={13} /> Edit
            </button>
          </div>
        </div>
      )}

      <div className="account-divider" />

      <h3 className="account-section-title">Change password</h3>
      {isChangingPassword ? (
        <form className="account-form" onSubmit={handleChangePassword} noValidate>
          <div className="account-field">
            <label>Current password</label>
            <PasswordInput
              autoComplete="current-password"
              placeholder="••••••••"
              value={pwForm.current_password}
              onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))}
              required
            />
          </div>
          <div className="account-field">
            <label>New password</label>
            <PasswordInput
              autoComplete="new-password"
              placeholder="••••••••"
              value={pwForm.new_password}
              onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
              required
            />
          </div>
          <div className="account-field">
            <label>Confirm new password</label>
            <PasswordInput
              autoComplete="new-password"
              placeholder="••••••••"
              value={pwForm.confirm_password}
              onChange={(e) => setPwForm((f) => ({ ...f, confirm_password: e.target.value }))}
              required
            />
          </div>
          {pwError && <p className="account-error">{pwError}</p>}
          <div className="account-form-actions">
            <button type="submit" className="account-btn" disabled={pwSaving}>
              {pwSaving ? 'Saving…' : 'Update password'}
            </button>
            <button
              type="button"
              className="account-btn account-btn--ghost"
              onClick={() => { setIsChangingPassword(false); setPwForm({ current_password: '', new_password: '', confirm_password: '' }); setPwError(''); }}
              disabled={pwSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          {pwSuccess && <p className="account-success" style={{ marginBottom: '12px' }}>{pwSuccess}</p>}
          <button
            className="account-btn account-btn--ghost"
            onClick={() => { setPwSuccess(''); setIsChangingPassword(true); }}
          >
            <Icon name="key" size={13} /> Change password
          </button>
        </>
      )}

      <div className="account-danger">
        <h3 className="account-danger__title">Danger zone</h3>
        <p className="account-danger__desc">
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        {showDeleteConfirm ? (
          <div className="account-delete-confirm">
            <span style={{ fontSize: 13.5, color: 'var(--fg)' }}>
              Are you sure? This action is irreversible.
            </span>
            <div className="account-delete-actions">
              <button className="account-btn account-btn--danger" onClick={handleDeleteAccount}>
                Yes, delete my account
              </button>
              <button className="account-btn account-btn--ghost" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="account-btn account-btn--danger-outline" onClick={() => setShowDeleteConfirm(true)}>
            <Icon name="trash-2" size={13} /> Delete account
          </button>
        )}
      </div>
    </div>
  );

  const handleLogout = async () => {
    try { await apiService.logout(); } finally { navigate('/'); }
  };

  return (
    <div className="account-root">
      <nav className="account-nav">
        <div className="account-nav__inner">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={18} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThemeToggle size={34} />
            <Btn size="sm" kind="ghost" icon="arrow-left" onClick={() => navigate('/app')}>
              Back to console
            </Btn>
          </div>
        </div>
      </nav>

      <div className="account-shell">
        <aside className="account-sidebar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`account-sidebar__item ${activeTab === tab.key ? 'account-sidebar__item--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon name={tab.icon} size={14} />
              {tab.label}
            </button>
          ))}
          {data?.user?.is_staff && (
            <>
              <div className="account-sidebar__divider" />
              {/* Staff only: the analytics API rejects everyone else, this just hides the door. */}
              <button className="account-sidebar__item" onClick={() => navigate('/app/application-analytics')}>
                <Icon name="activity" size={14} />
                Application analytics
              </button>
            </>
          )}
          <div className="account-sidebar__divider" />
          <button className="account-sidebar__item account-sidebar__item--danger" onClick={handleLogout}>
            <Icon name="log-out" size={14} />
            Log out
          </button>
        </aside>

        <main className="account-main">
          {error && <p className="account-error">{error}</p>}
          <section className="account-card">
            <div className="account-card__head">
              <h1 className="account-card__title">
                {tabs.find((t) => t.key === activeTab)?.label}
              </h1>
            </div>
            {activeTab === 'organization' && renderOrganization()}
            {activeTab === 'connectors' && <ConnectorsPanel />}
            {activeTab === 'usage' && renderUsage()}
            {activeTab === 'api-keys' && <ApiKeysPanel />}
            {activeTab === 'settings' && renderSettings()}
          </section>
        </main>
      </div>
    </div>
  );
};

export default AccountPage;
